import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import PopupTargetController from "./PopupTargetController";
import NotificationType from "../classes/NotificationType";
import Popup from "./Popup";
import ProfileView from "./ProfileView";
import NotificationView from "./NotificationView";
import MaterialIcons from "../icons/MaterialIcons";
import UserType from "../classes/UserType";
import Notifications from "../classes/Notifications";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import MessageDispatch from "../rocket/MessageDispatch";
import ListWrapper from "../utils/ListWrapper";
import NotificationsRequest from "../models/NotificationsRequest";
import Notification from "../models/Notification";
import IconWithCount from "./IconWithCount";
import Administrator from "../models/Administrator";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface AdminHeaderViewProps extends BaseUIProps {
  key?: string;
  admin: Administrator;
  user: NOKUser;
}

class _AdminHeaderViewState extends ObservableComponent<AdminHeaderViewProps> {
  static defaultProps = { admin: null, user: null };
  query: Notifications = null;
  notifications: Array<Notification> = ListWrapper.widget(
    this,
    "notifications"
  );
  request: NotificationsRequest = null;
  unReadNotifications: Array<Notification> = ListWrapper.widget(
    this,
    "unReadNotifications"
  );
  notificationPopupPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public iconButtonPopupTargetController: PopupTargetController =
    new PopupTargetController();
  public constructor(props: AdminHeaderViewProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.hospital",
        "request.orderBy",
        "request.status",
        "request.user",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeNotifications);

    this.computeNotifications();

    this.on(
      ["notifications", "notifications.viewed"],
      this.computeUnReadNotifications
    );

    this.computeUnReadNotifications();

    this.on(
      [
        "admin",
        "admin.email",
        "admin.name",
        "unReadNotifications",
        "user",
        "user.email",
        "user.hospital",
        "user.hospital.name",
        "user.name",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminHeaderViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setQuery(val: Notifications): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getNotifications(
          UsageConstants.QUERY_GETNOTIFICATIONS_ADMINHEADERVIEW_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setNotifications(val: Array<Notification>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.notifications,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("notifications", this.notifications, val);

    this.notifications.clear();

    this.notifications.addAll(val);

    this.fire("notifications", this);
  }
  public addToNotifications(val: Notification, index: number = -1): void {
    if (index === -1) {
      if (!this.notifications.contains(val)) this.notifications.add(val);
    } else {
      this.notifications.remove(this.notifications.elementAt(index));

      this.notifications.add(val);
    }

    this.fire("notifications", this, val, true);

    this.updateObservable("notifications", null, val);
  }
  public removeFromNotifications(val: Notification): void {
    this.notifications.remove(val);

    this.fire("notifications", this, val, false);

    this.removeObservable("notifications", val);
  }
  public computeNotifications = (): void => {
    try {
      this.setNotifications(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(
        " exception in computeNotifications : " + exception.toString()
      );

      this.setNotifications([]);
    }
  };
  public setRequest(val: NotificationsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setUnReadNotifications(val: Array<Notification>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.unReadNotifications,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl(
      "unReadNotifications",
      this.unReadNotifications,
      val
    );

    this.unReadNotifications.clear();

    this.unReadNotifications.addAll(val);

    this.fire("unReadNotifications", this);
  }
  public addToUnReadNotifications(val: Notification, index: number = -1): void {
    if (index === -1) {
      if (!this.unReadNotifications.contains(val))
        this.unReadNotifications.add(val);
    } else {
      this.unReadNotifications.remove(
        this.unReadNotifications.elementAt(index)
      );

      this.unReadNotifications.add(val);
    }

    this.fire("unReadNotifications", this, val, true);

    this.updateObservable("unReadNotifications", null, val);
  }
  public removeFromUnReadNotifications(val: Notification): void {
    this.unReadNotifications.remove(val);

    this.fire("unReadNotifications", this, val, false);

    this.removeObservable("unReadNotifications", val);
  }
  public computeUnReadNotifications = (): void => {
    try {
      this.setUnReadNotifications(
        Array.from(
          this.notifications.where((element) => !element.viewed).toList()
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeUnReadNotifications : " + exception.toString()
      );

      this.setUnReadNotifications([]);
    }
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            this.admin !== null ? ui.Container({}) : [],
            this.user !== null
              ? ui.Row({
                  children: [
                    TextView({
                      data: this.user.hospital.name,
                      style: new ui.TextStyle({
                        fontSize: 20,
                        fontWeight: ui.FontWeight.bold,
                      }),
                      className: "x3f4",
                      key: "0",
                    }),
                  ],
                })
              : [],
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.end,
              children: [
                this.user !== null &&
                (this.user.userType === UserType.Admin ||
                  this.user.userType === UserType.Staff)
                  ? ui.Container({
                      margin: ui.EdgeInsets.fromLTRB(
                        0.0,
                        0.0,
                        10.0,
                        0.0,
                        new Map()
                      ),
                      padding: ui.EdgeInsets.all(10.0, new Map()),
                      child: IconWithCount({
                        icon: MaterialIcons.notifications,
                        countBgColor: cStyle.c25,
                        count: this.unReadNotifications.length.toString(),
                        onPressed: () => {
                          this.notificationHandler();
                        },
                        d3eRef: this.iconButtonPopupTargetController.handleRef,
                      }),
                      className: "x185",
                    })
                  : [],
                ProfileView({
                  name: this.admin !== null ? this.admin.name : this.user.name,
                  email:
                    this.admin !== null ? this.admin.email : this.user.email,
                  textColor: cStyle.c19,
                  iconColor: cStyle.c19,
                  cardBgColor: cStyle.c21,
                  key: "1",
                }),
              ],
              key: "2",
            }),
          ],
          className: "x3a9 hc h",
          key: "0",
        }),
      ],
      className: ui.join(this.props.className, "AdminHeaderView x576 hc h"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    if (this.user !== null) {
      if (this.user.userType === UserType.Admin) {
        this.setRequest(
          new NotificationsRequest({
            orderBy: "sortOrder",
            ascending: false,
            applyStatus: true,
            status: NotificationType.values,
            hospital: this.user.hospital,
          })
        );
      } else {
        this.setRequest(
          new NotificationsRequest({
            orderBy: "sortOrder",
            ascending: false,
            applyStatus: true,
            status: [NotificationType.ServiceType],
            hospital: this.user.hospital,
            user: this.user,
          })
        );
      }
    }
  };
  public notificationHandler = (): void => {
    if (this.unReadNotifications.length !== 0) {
      this.showNotificationPopup({ autoClose: true });
    }
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    this.notificationPopupPopup?.dispose();

    super.dispose();
  }
  public showNotificationPopup(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.notificationPopupPopup?.dispose();

    let target: ui.Rect = this.iconButtonPopupTargetController.getTarget(
      this.context
    );

    this.notificationPopupPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Left,
      child: ui.Container({
        height: 300,
        width: 400,
        child: NotificationView({ user: this.user, admin: this.admin }),
        className: "x10b hc vc",
      }),
      target: target,
    });

    this.notificationPopupPopup.showPopup(this.context);
  }
  public hideNotificationPopup(): void {
    this.notificationPopupPopup?.dispose();
  }
}
export default function AdminHeaderView(props: AdminHeaderViewProps) {
  return React.createElement(_AdminHeaderViewState, {
    ..._AdminHeaderViewState.defaultProps,
    ...props,
  });
}
